/**
 * API клиент для управления токенами регистрации устройств
 * Дата создания: 29 июля 2025, 20:15 MSK
 */

import { api } from './api';

// Типы для токенов регистрации
export interface RegistrationTokenCreate {
  name: string;
  location?: string;
  device_type: 'tv' | 'monitor' | 'tablet' | 'led_panel' | 'video_wall' | 'desktop_pc' | 'raspberry_pi' | 'intel_nuc' | 'android_tv';
  description?: string;
  expires_hours?: number;
}

export interface RegistrationToken {
  id: number;
  token: string;
  name: string;
  location?: string;
  device_type: string;
  description?: string;
  expires_at: string;
  created_at: string;
  is_used: boolean;
  used_at?: string;
  used_by_device_id?: number;
  created_by_user_id: number;
}

export interface RegistrationTokenList {
  id: number;
  token: string; // Первые 8 символов + "..."
  name: string;
  location?: string;
  device_type: string;
  expires_at: string;
  created_at: string;
  is_used: boolean;
  used_at?: string;
  used_by_device_id?: number;
  created_by_user_id: number;
}

export interface RegistrationTokenStatus {
  id: number;
  is_valid: boolean;
  is_used: boolean;
  is_expired: boolean;
  expires_at: string;
  used_at?: string;
  used_by_device_id?: number;
}

export interface DeviceRegistrationRequest {
  registration_token: string;
  device_info: Record<string, any>;
  player_version: string;
}

export interface DeviceRegistrationResponse {
  device_id: number;
  api_key: string;
  device_uuid: string;
  name: string;
  message: string;
}

// Типы для API ключей устройств
export interface DeviceApiKeyCreate {
  name: string;
  description?: string;
  expires_hours?: number;
}

export interface DeviceApiKey {
  id: number;
  device_id: number;
  key_prefix: string;
  name: string;
  description?: string;
  created_at: string;
  expires_at?: string;
  last_used_at?: string;
  is_active: boolean;
  is_expired: boolean;
  created_by_user_id: number;
}

export interface DeviceApiKeyResponse {
  id: number;
  device_id: number;
  api_key: string; // Полный ключ (только при создании)
  name: string;
  description?: string;
  expires_at?: string;
  created_at: string;
  is_active: boolean;
}

export interface DeviceApiKeyUpdate {
  name?: string;
  description?: string;
  is_active?: boolean;
}

export interface DeviceApiKeyRevoke {
  reason?: string;
}

// Параметры запросов
export interface RegistrationTokensParams {
  skip?: number;
  limit?: number;
  include_used?: boolean;
  include_expired?: boolean;
}

export interface DeviceApiKeysParams {
  include_inactive?: boolean;
}

/**
 * API клиент для токенов регистрации
 */
export const registrationTokenApi = {
  // Управление токенами регистрации
  async getTokens(params?: RegistrationTokensParams): Promise<RegistrationTokenList[]> {
    const searchParams = new URLSearchParams();
    if (params?.skip) searchParams.append('skip', params.skip.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.include_used !== undefined) searchParams.append('include_used', params.include_used.toString());
    if (params?.include_expired !== undefined) searchParams.append('include_expired', params.include_expired.toString());
    
    return api.get(`/registration-tokens/?${searchParams.toString()}`);
  },

  async createToken(data: RegistrationTokenCreate): Promise<RegistrationToken> {
    return api.post('/registration-tokens/', data);
  },

  async getToken(tokenId: number): Promise<RegistrationToken> {
    return api.get(`/registration-tokens/${tokenId}`);
  },

  async getTokenStatus(tokenId: number): Promise<RegistrationTokenStatus> {
    return api.get(`/registration-tokens/${tokenId}/status`);
  },

  async revokeToken(tokenId: number): Promise<RegistrationToken> {
    return api.put(`/registration-tokens/${tokenId}/revoke`, {});
  },

  async getStatistics(): Promise<{
    total: number;
    active: number;
    used: number;
    expired: number;
  }> {
    return api.get('/registration-tokens/statistics/overview');
  },

  async cleanupExpired(): Promise<{ message: string; deleted_count: number }> {
    return api.delete('/registration-tokens/cleanup');
  },

  // Регистрация устройства (используется плеерами)
  async registerDevice(data: DeviceRegistrationRequest): Promise<DeviceRegistrationResponse> {
    return api.post('/registration-tokens/register', data);
  },
};

/**
 * API клиент для API ключей устройств
 */
export const deviceApiKeyApi = {
  // Управление API ключами
  async getDeviceApiKeys(deviceId: number, params?: DeviceApiKeysParams): Promise<DeviceApiKey[]> {
    const searchParams = new URLSearchParams();
    if (params?.include_inactive !== undefined) searchParams.append('include_inactive', params.include_inactive.toString());
    
    return api.get(`/devices/${deviceId}/api-keys?${searchParams.toString()}`);
  },

  async createApiKey(deviceId: number, data: DeviceApiKeyCreate): Promise<DeviceApiKeyResponse> {
    return api.post(`/devices/${deviceId}/api-keys`, data);
  },

  async getApiKey(keyId: number): Promise<DeviceApiKey> {
    return api.get(`/devices/api-keys/${keyId}`);
  },

  async updateApiKey(keyId: number, data: DeviceApiKeyUpdate): Promise<DeviceApiKey> {
    return api.put(`/devices/api-keys/${keyId}`, data);
  },

  async revokeApiKey(keyId: number, data?: DeviceApiKeyRevoke): Promise<DeviceApiKey> {
    return api.put(`/devices/api-keys/${keyId}/revoke`, data || {});
  },

  async rotateDeviceKeys(deviceId: number): Promise<DeviceApiKeyResponse> {
    return api.post(`/devices/${deviceId}/api-keys/rotate`, {});
  },

  async getApiKeyStatistics(deviceId: number): Promise<{
    total: number;
    active: number;
    expired: number;
    recently_used: number;
    inactive: number;
  }> {
    return api.get(`/devices/${deviceId}/api-keys/statistics`);
  },

  async cleanupExpiredKeys(): Promise<{ message: string; deactivated_count: number }> {
    return api.delete('/devices/cleanup/expired');
  },
};

// Дефолтный экспорт для обратной совместимости
export default { registrationTokenApi, deviceApiKeyApi }; 